<template>
  <div class="tenant-detail-card">
    <div class="detail-header">
      <div class="header-main">
        <div class="header-icon-box">
          <i class="fas fa-building"></i>
        </div>
        <div class="header-text">
          <h3 class="detail-title">Tenant Overview</h3>
          <p class="detail-subtitle">In-depth details for the currently selected tenant.</p>
        </div>
      </div>
      <div class="header-status">
        <span 
          class="status-pill" 
          :class="tenant.status === 'active' ? 'active' : 'inactive'">
          <i class="fas fa-check-circle" v-if="tenant.status === 'active'"></i>
          <i class="fas fa-times-circle" v-else></i>
          {{ tenant.status }}
        </span>
      </div>
    </div>

    <div class="card-body">
      <div class="detail-grid">
        <!-- Corporate Name -->
        <div class="detail-item">
          <div class="detail-label">
            <i class="fas fa-building"></i>
            <span>Corporate Name</span>
          </div>
          <div class="detail-value">{{ tenant.name }}</div>
        </div>

        <!-- Corporate Code -->
        <div class="detail-item">
          <div class="detail-label">
            <i class="fas fa-tag"></i>
            <span>Corporate Code</span>
          </div>
          <div class="detail-value">
            <span class="code-badge">{{ tenant.code }}</span>
          </div>
        </div>

        <!-- Domain -->
        <div class="detail-item">
          <div class="detail-label">
            <i class="fas fa-globe"></i>
            <span>Domain</span>
          </div>
          <div class="detail-value">
            <a :href="`http://${tenant.domain}`" target="_blank" class="domain-link">
              {{ tenant.domain }}
              <i class="fas fa-external-link-alt ms-1"></i>
            </a>
          </div>
        </div>

        <!-- Tenant ID -->
        <div class="detail-item">
          <div class="detail-label">
            <i class="fas fa-fingerprint"></i>
            <span>Tenant ID</span>
          </div>
          <div class="detail-value">
            <code class="tenant-id">{{ tenant.tenant_id || tenant.id }}</code>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="card-actions">
        <router-link 
          :to="{ name: 'ExtranetCorporateEdit', params: { id: tenant.id } }"
          class="btn btn-outline-primary">
          <i class="fas fa-edit me-2"></i>
          Edit Corporate
        </router-link>
        
        <button @click="clearSelection" class="btn btn-outline-secondary">
          <i class="fas fa-times me-2"></i>
          Clear Selection
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex'

export default {
  name: 'TenantDetailCard',

  props: {
    tenant: {
      type: Object,
      required: true
    }
  },

  setup() {
    const store = useStore()

    function clearSelection() {
      if (confirm('Are you sure you want to clear tenant selection? This will reload the page.')) {
        store.dispatch('extranet/clearTenant')
        window.location.reload()
      }
    }

    return {
      clearSelection
    }
  }
}
</script>

<style scoped>
/* Card Container */
.tenant-detail-card {
  background: white;
  border-radius: var(--extranet-rounded);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

/* Detail Header */
.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 32px;
  background-color: #ffffff;
  border-bottom: 1px solid #f1f5f9;
}

.header-main {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-icon-box {
  width: 52px;
  height: 52px;
  background: #fff5f5;
  border-radius: var(--extranet-rounded);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--extranet-primary);
  font-size: 22px;
}

.detail-title {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.detail-subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-pill.active {
  background: #ecfdf5;
  color: #059669;
}

.status-pill.inactive {
  background: #f1f5f9;
  color: #64748b;
}

/* Card Body */
.card-body {
  padding: 32px;
}

/* Detail Grid */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  margin-bottom: 40px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
}

.detail-value {
  font-size: 14px;
  color: #334155;
  font-weight: 600;
  word-break: break-all;
}

/* Code Badge */
.code-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #1e293b;
  border-radius: 6px;
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 12px;
}

/* Domain Link */
.domain-link {
  color: var(--extranet-primary);
  text-decoration: none;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.domain-link:hover {
  text-decoration: underline;
}

/* Tenant ID */
.tenant-id {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}

/* Card Actions */
.card-actions {
  display: flex;
  gap: 16px;
  padding-top: 32px;
  border-top: 1px solid #f1f5f9;
}

.btn {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: var(--extranet-rounded);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  text-decoration: none;
  gap: 10px;
}

.btn-outline-primary {
  background: var(--extranet-primary);
  color: white;
}

.btn-outline-primary:hover {
  filter: brightness(0.9);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(181, 26, 31, 0.15);
}

.btn-outline-secondary {
  background: #f1f5f9;
  color: #475569;
}

.btn-outline-secondary:hover {
  background: #e2e8f0;
  color: #1e293b;
}

/* Responsive */
@media (max-width: 1200px) {
  .detail-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .detail-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 24px;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .card-body {
    padding: 24px;
  }

  .card-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
